import { ITagStatus } from '@tecalisdev/infinity';
import { formatExpirationDate } from '../../core/utils/utils';
import { BOOLEAN_STATUS, IOperationOcrDetail, PROCESS_STATUS } from '../../processes/models/processes.models';
import { MenuItemAction } from '../../shared/models/master.models';
import {
	IFormOperationAnswerStep,
	IOperationActivity,
	IOperationFormDetail,
	OPERATION_STATUS,
} from '../models/operations.models';
import {
	resolveFormOperationOcrPayload,
	resolveFormStepActivityEvents,
	resolveFormStepActivityTitle,
} from './form-operation-detail-contract.util';
import { hasFormFieldMatchOcrKo } from './form-operation-detail-display.util';
import {
	buildFormMatchDataErrorKeySet,
	hasFormOperationMatchDataKo,
	hasFormStepActivityKo,
	resolveFormMatchDataErrors,
} from './form-operation-match-data.util';
import { listFormMatchCompareFields, resolveFormMatchDataLabelKey } from './form-operation-match-compare.util';

export interface FormActivityLogContext {
	startAt?: string | null;
	endAt?: string | null;
	topLevelState?: string | null;
	statusReasons?: string | null;
	logsUrl?: string | null;
	locale: string;
}

export interface FormActivityLogDeps {
	translate: (key: string, params?: Record<string, unknown>) => string;
	shouldDisplayAntifraudCheck: (
		operationState: string | undefined,
		antifraudStatus: BOOLEAN_STATUS | string | undefined,
		options?: { showOnlyKo?: boolean },
	) => boolean;
	getTagStatus: (state: string) => ITagStatus;
}

/** Construye filas de actividad FORM (Figma `9257-173889`). */
export function appendFormOperationActivityLogs(
	activity: IOperationActivity[],
	form: IOperationFormDetail,
	context: FormActivityLogContext,
	deps: FormActivityLogDeps,
): void {
	const startAt = context.startAt ?? form.start_at;
	if (startAt) {
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.INIT_OPERATION'),
			date: startAt,
			...(context.logsUrl ? { showLogs: true } : {}),
		});
	}

	for (const step of resolveFormStepActivityEvents(form)) {
		appendFormStepActivityRow(activity, form, step, context, deps);
	}

	appendFormMatchDataActivityRow(activity, form, context, deps);

	const ocr = resolveFormOperationOcrPayload(form);
	if (ocr) {
		appendFormOcrCheckActivityRows(activity, ocr, context, deps);
	}

	const state = normalizeFormActivityState(form.state ?? context.topLevelState);
	appendFormManualReviewActivityRows(activity, state, context, deps);
}

function appendFormStepActivityRow(
	activity: IOperationActivity[],
	form: IOperationFormDetail,
	step: ReturnType<typeof resolveFormStepActivityEvents>[number],
	context: FormActivityLogContext,
	deps: FormActivityLogDeps,
): void {
	const title = resolveFormStepActivityTitle(step.title, context.locale);
	const snapshot = step.response_snapshot as IFormOperationAnswerStep | undefined;
	const matchDataErrorKeys = buildFormMatchDataErrorKeySet(resolveFormMatchDataErrors(form));
	const hasMismatch =
		!!snapshot &&
		(hasFormFieldMatchOcrKo(snapshot.fields) || hasFormStepActivityKo(snapshot, matchDataErrorKeys));
	const effectiveState = hasMismatch ? OPERATION_STATUS.KO : step.state;
	const stateKey = String(effectiveState ?? '').toUpperCase();
	const status =
		stateKey === OPERATION_STATUS.KO
			? BOOLEAN_STATUS.KO
			: stateKey === OPERATION_STATUS.OK
				? BOOLEAN_STATUS.OK
				: undefined;

	activity.push({
		title,
		icon: 'icon-fill-form',
		...(step.completed_at ? { date: step.completed_at } : {}),
		...(status ? { status } : {}),
		...(snapshot
			? {
					link: {
						label: deps.translate('GENERAL.SHOW_DETAILS'),
						action: MenuItemAction.SHOW_FORM_STEP,
						info: {
							title: title || deps.translate('OPERATIONS.DETAIL.FORM.ACTIVITY_JSON_TITLE'),
							step: snapshot,
							config: form.config,
							matchDataErrors: resolveFormMatchDataErrors(form),
						},
					},
				}
			: {}),
	});
}

function appendFormMatchDataActivityRow(
	activity: IOperationActivity[],
	form: IOperationFormDetail,
	context: FormActivityLogContext,
	deps: FormActivityLogDeps,
): void {
	if (!listFormMatchCompareFields(form).length || !hasFormOperationMatchDataKo(form)) {
		return;
	}
	const { translate } = deps;
	const ocr = resolveFormOperationOcrPayload(form);
	activity.push({
		title: translate(resolveFormMatchDataLabelKey(form, ocr)),
		icon: 'icon-resize-viewport',
		status: BOOLEAN_STATUS.KO,
		link: {
			label: translate('GENERAL.SHOW_DETAILS'),
			action: MenuItemAction.SHOW_FORM_MATCH_COMPARE,
			info: {
				form,
				config: form.config,
				matchDataErrors: resolveFormMatchDataErrors(form),
			},
		},
	});
}

function appendFormOcrCheckActivityRows(
	activity: IOperationActivity[],
	ocr: IOperationOcrDetail,
	context: FormActivityLogContext,
	deps: FormActivityLogDeps,
): void {
	const { translate, shouldDisplayAntifraudCheck } = deps;

	if (shouldDisplayAntifraudCheck(ocr.state, ocr.antifraud?.EXPIRATED)) {
		activity.push({
			title: translate('PROCESSES.DETAIL.DOCUMENT_EXPIRED'),
			description: ocr.expiration_date
				? [formatExpirationDate(ocr.expiration_date, 'dd/MM/yyyy', translate('GENERAL.PERMANENT'))]
				: [],
			icon: 'icon-list-ticks',
			status: ocr.antifraud?.EXPIRATED,
		});
	}

	if (shouldDisplayAntifraudCheck(ocr.state, ocr.antifraud?.AEAT)) {
		const aeatError =
			ocr.antifraud?.AEAT === BOOLEAN_STATUS.KO ? translate('PROCESSES.DETAIL.AEAT_ERROR') : '';
		activity.push({
			title: translate('PROCESSES.DETAIL.AEAT'),
			...(aeatError ? { description: [aeatError] } : {}),
			icon: 'icon-resize-viewport',
			status: ocr.antifraud?.AEAT,
			link: {
				label: translate('GENERAL.SHOW_DETAILS'),
				action: MenuItemAction.SHOW_AEAT_COMPARE,
				info: { ocr },
			},
		});
	}
}

function appendFormManualReviewActivityRows(
	activity: IOperationActivity[],
	state: string | undefined,
	context: FormActivityLogContext,
	deps: FormActivityLogDeps,
): void {
	const endAt = context.endAt;
	const { translate, getTagStatus } = deps;

	if (state === PROCESS_STATUS.PENDING) {
		activity.push({
			...(endAt ? { date: endAt } : {}),
			description: [translate('OPERATIONS.DETAIL.STATE_MESSAGES.REVIEW')],
			tag: {
				label: translate('OPERATIONS.DETAIL.FORM.ACTIVITY_TAG_REVIEW'),
				status: getTagStatus(PROCESS_STATUS.PENDING),
			},
		});
		return;
	}

	const reviewer = resolveManualReviewerName(context.statusReasons);
	if (reviewer && state === OPERATION_STATUS.OK) {
		activity.push({
			...(endAt ? { date: endAt } : {}),
			description: [translate('OPERATIONS.DETAIL.FORM.ACTIVITY_MANUAL_APPROVED', { user: reviewer })],
			tag: {
				label: translate('OPERATIONS.DETAIL.FORM.ACTIVITY_TAG_APPROVED'),
				status: getTagStatus(OPERATION_STATUS.OK),
			},
		});
	}
}

function normalizeFormActivityState(state: string | null | undefined): string | undefined {
	if (state === undefined || state === null) {
		return undefined;
	}
	return String(state).toUpperCase();
}

/** Extrae nombre de revisor desde `status_reasons` (texto libre o «… por Nombre»). */
export function resolveManualReviewerName(statusReasons?: string | null): string | null {
	const trimmed = statusReasons?.trim();
	if (!trimmed) {
		return null;
	}
	const porMatch = trimmed.match(/por\s+(.+)$/i);
	if (porMatch?.[1]?.trim()) {
		return porMatch[1].trim();
	}
	if (trimmed.length > 2 && !trimmed.startsWith('{')) {
		return trimmed;
	}
	return null;
}
